import { Directive, signal, type Signal } from '@angular/core';
import {
  type AbstractControl,
  FormArray,
  FormControl,
  type FormGroup,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import type { LanguageCode } from '../model/language-code';
import { AbstractDetailsComponent } from './abstract-details.component';
import type { EntityFormGroup } from '@pct-ng/form';
import { isString } from '@pct-ts/typeguard';
import { extractTextFromHtml } from '@pct-ng/feature-rich-text-tip-tap';
import { deepEqual } from 'fast-equals';
import type { ImagePutDTO } from '../model/image-put-dto';

@Directive()
export abstract class AbstractTranslatableDetailsComponent<
  GET_DTO,
  DTO_ID_KEY extends keyof GET_DTO,
  LIST_DTO,
  PUT_DTO,
  FORM extends { [K in keyof PUT_DTO]: AbstractControl },
  TRANSLATION_PUT_DTO extends { language: LanguageCode },
  TRANSLATIONS_FORM_GROUP extends FormGroup<EntityFormGroup<TRANSLATION_PUT_DTO, never, any>>,
> extends AbstractDetailsComponent<GET_DTO, DTO_ID_KEY, LIST_DTO, PUT_DTO, FORM> {
  protected abstract readonly excludeRequiredValidationFields: string[];
  protected abstract readonly requiredTranslationFields: string[];
  protected readonly selectedLanguageControl = new FormControl<LanguageCode>('hu', {
    nonNullable: true,
  });
  protected readonly selectedTranslationsFormGroup: Signal<TRANSLATIONS_FORM_GROUP>;
  protected readonly translationsFormArray: FormArray<TRANSLATIONS_FORM_GROUP>;

  /**
   * Ez egy folyamatosan frissülő változó amely azoknak a nyelveknek a kódjai-t tartalmazza,
   * amely nyelveken fordítását felhasználó megkezdte
   */
  readonly #startedLanguages = signal<LanguageCode[]>([]);

  get startedLanguages(): Signal<LanguageCode[]> {
    return this.#startedLanguages.asReadonly();
  }

  constructor() {
    super();

    this.translationsFormArray = new FormArray<TRANSLATIONS_FORM_GROUP>([
      this.#createTranslationForm('hu'),
      this.#createTranslationForm('en'),
    ]);

    this.selectedTranslationsFormGroup = toSignal(
      this.selectedLanguageControl.valueChanges.pipe(
        map(selected => {
          const result = (this.translationsFormArray.controls as TRANSLATIONS_FORM_GROUP[]).find(
            group => this.getLanguageValueFromTranslationGroup(group) === selected,
          );
          if (result === undefined) {
            console.error(`No translation found for language: ${selected}`);
            return this.translationsFormArray.at(0) as TRANSLATIONS_FORM_GROUP;
          }
          return result;
        }),
      ),
      {
        initialValue:
          (this.translationsFormArray.controls as TRANSLATIONS_FORM_GROUP[]).find(
            group => this.getLanguageValueFromTranslationGroup(group) === 'hu',
          ) ?? (this.translationsFormArray.at(0) as TRANSLATIONS_FORM_GROUP),
      },
    );
  }

  protected updateTranslationFieldsRequiredValidation(): void {
    const groups = this.translationsFormArray.controls as TRANSLATIONS_FORM_GROUP[];
    const groupsWithValues = groups.map((group, index) => ({
      group,
      index,
      hasValue: this.hasTranslationFormGroupRequiredValue(group),
    }));

    const startedLanguages = groupsWithValues
      .filter(({ hasValue }) => hasValue)
      .map(({ group }) => group.controls.language.value);

    //Csak akkor frissítjük a startedLanguages, ha tényleg változott
    if (!deepEqual(startedLanguages, this.#startedLanguages())) {
      this.#startedLanguages.set(startedLanguages);
    }

    // Ellenőrizzük, hogy van-e bármely csoportnak értéke
    const hasAnyGroupWithValue = groupsWithValues.some(g => g.hasValue);

    groups.forEach((group, index) => {
      const groupHasValue = groupsWithValues[index].hasValue;

      Object.entries(group.controls)
        .filter(([name]) => !this.excludeRequiredValidationFields.includes(name))
        .forEach(([, control]) => {
          if (control !== null) {
            control.removeValidators(Validators.required);

            // Ha ennek a csoportnak van értéke, vagy ha egyetlen csoportnak sincs értéke,
            // akkor required-dé tesszük a mezőt
            if (groupHasValue || !hasAnyGroupWithValue) {
              control.addValidators(Validators.required);
            }

            control.updateValueAndValidity({ emitEvent: false });
          }
        });
    });
  }

  protected hasTranslationFormGroupRequiredValue(group: FormGroup): boolean {
    return this.hasAnyValue(group, this.requiredTranslationFields);
  }

  protected hasAnyValue(group: FormGroup, fieldNames: string[]): boolean {
    return Object.entries(group.controls).some(([key, control]) => {
      if (!fieldNames.includes(key)) {
        return false;
      }
      const value = control.value;
      const result = Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined && value !== '';
      if (result === true) {
        if (isString(value) && value.charAt(0) === '<' && extractTextFromHtml(value) === '') {
          return false;
        }
      }
      return result;
    });
  }

  #createTranslationForm(language: LanguageCode): TRANSLATIONS_FORM_GROUP {
    const formGroup = this.createTranslationForm(language);
    formGroup.valueChanges.subscribe(() => this.updateTranslationFieldsRequiredValidation());
    return formGroup;
  }

  protected override editModePatchValue(dto: GET_DTO): void {
    const removed: TRANSLATIONS_FORM_GROUP[] = [];
    const translationsCount = this.getTranslationsCount(dto);
    if (translationsCount > 0) {
      const languageCode = this.getFirstTranslationLanguageCode(dto) as LanguageCode;
      if (translationsCount === 1) {
        const removeIndexes: number[] = [];
        this.translationsFormArray.controls.forEach((group, index) => {
          if (
            this.getLanguageValueFromTranslationGroup(group as TRANSLATIONS_FORM_GROUP) !==
            languageCode
          ) {
            removeIndexes.push(index);
          }
        });
        if (removeIndexes.length > 0) {
          removeIndexes.reverse().forEach(index => {
            removed.push(this.translationsFormArray.at(index) as TRANSLATIONS_FORM_GROUP);
            this.translationsFormArray.removeAt(index);
          });
        }
      }
      this.selectedLanguageControl.setValue(languageCode);
    }

    super.editModePatchValue(dto);
    if (removed.length > 0) {
      removed.forEach(index => this.translationsFormArray.push(index));
    }
    this.updateTranslationFieldsRequiredValidation();
  }

  protected getFilteredTranslations(translations: TRANSLATION_PUT_DTO[]): TRANSLATION_PUT_DTO[] {
    const startedLanguages = this.startedLanguages();
    return translations.filter(({ language }) => startedLanguages.includes(language));
  }
  protected getFilteredImages(images: ImagePutDTO[] | null): ImagePutDTO[] | null {
    const startedLanguages = this.startedLanguages();
    return (
      images?.map(image => ({
        ...image,
        translations: startedLanguages.map(startedLanguage => ({
          language: startedLanguage,
          subtitle:
            image.translations?.find(({ language }) => language === startedLanguage)?.subtitle ??
            '',
        })),
      })) ?? null
    );
  }

  protected abstract createTranslationForm(language: LanguageCode): TRANSLATIONS_FORM_GROUP;
  protected abstract getLanguageValueFromTranslationGroup(
    formGroup: TRANSLATIONS_FORM_GROUP,
  ): LanguageCode;
  protected abstract getFirstTranslationLanguageCode(dto: GET_DTO): string;
  protected abstract getTranslationsCount(dto: GET_DTO): number;
}
